import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";

const Privacy = () => {
  const navigate = useNavigate();
  const effectiveDate = "January 15, 2025";

  return (
    <div className="min-h-screen bg-gradient-radial from-background via-background to-muted/20">
      <SEO 
        title="Privacy Policy | Neil AI Global Solutions"
        description="Learn how Neil AI Global Solutions protects your privacy and handles your personal information. Our comprehensive privacy policy explains data collection, usage, and security practices."
        keywords="privacy policy, data protection, personal information, Neil AI, privacy rights, GDPR, CCPA, data security"
      />
      <Navigation />
      
      <div className="container max-w-4xl mx-auto px-4 py-16">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="text-center mb-12">
          <h1 className="font-orbitron text-4xl md:text-5xl font-bold mb-4">
            <span className="text-primary">Privacy</span> Policy
          </h1>
          <p className="text-muted-foreground text-lg">
            Effective Date: {effectiveDate}
          </p>
          <p className="text-muted-foreground mt-2">
            Your privacy and data security are our top priorities
          </p>
        </div>

        <div className="space-y-6">
          {/* Introduction */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">1.</span> Introduction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                Welcome to Neil AI Global Solutions LLC ("we," "our," "us"). 
                We value your privacy and are committed to protecting your personal information. 
                This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website, 
                use our AI tools, schedule demos, or interact with our digital assistants.
              </p>
              <p className="font-semibold text-foreground">
                By using our website or services, you agree to this policy. If you disagree, 
                please discontinue use of our services.
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">2.</span> Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                We collect information to improve your experience and provide personalized AI solutions.
              </p>
              
              <div>
                <h3 className="font-bold text-foreground mb-2">A. Information You Provide</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Name, email, and phone number (e.g., when booking a demo or contacting support)</li>
                  <li>Business information (company name, role, industry)</li>
                  <li>Payment or billing details (when purchasing subscriptions or hardware)</li>
                  <li>Messages and voice recordings shared with our AI assistant</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-foreground mb-2">B. Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Device type, browser, and operating system</li>
                  <li>IP address and approximate location</li>
                  <li>Pages visited, time spent, and referring links</li>
                  <li>Voice/audio data if you use our voice assistant widget</li>
                  <li>Cookies and analytics data (see Section 6)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Your Information */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">3.</span> How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>We use your data to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Provide and personalize our AI services</li>
                <li>Process orders, subscriptions, or demos</li>
                <li>Improve our website performance and AI accuracy</li>
                <li>Communicate updates, offers, and support information</li>
                <li>Ensure security, fraud prevention, and compliance with laws</li>
              </ul>
              <p className="font-semibold text-foreground">
                We never sell your personal data to third parties.
              </p>
            </CardContent>
          </Card>

          {/* Sharing of Information */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">4.</span> Sharing of Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>We may share limited information with:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Service providers (hosting, analytics, payment processing)</li>
                <li>AI technology partners (for improving automation accuracy)</li>
                <li>Legal authorities (if required by law or to protect rights and safety)</li>
              </ul>
              <p>
                All partners are required to maintain strict confidentiality and comply with privacy regulations.
              </p>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">5.</span> Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>Your data is protected using:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>SSL encryption for all website traffic</li>
                <li>Secure data centers with limited access</li>
                <li>Regular security audits and AI safety checks</li>
                <li>Role-based access control for employees and partners</li>
              </ul>
              <p>
                Despite our best efforts, no online transmission is 100% secure, but we continually update our systems 
                for maximum safety.
              </p>
            </CardContent>
          </Card>

          {/* Cookies & Tracking */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">6.</span> Cookies & Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>Our website uses cookies to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Remember preferences (e.g., login sessions)</li>
                <li>Track usage for analytics and performance improvements</li>
                <li>Deliver more personalized recommendations through AI</li>
              </ul>
              <p>
                You can disable cookies in your browser settings at any time.
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">7.</span> Your Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>Depending on your location (GDPR, CCPA), you may:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Request a copy of your personal data</li>
                <li>Ask to correct or delete your information</li>
                <li>Opt out of marketing communications</li>
                <li>Withdraw consent for data collection at any time</li>
              </ul>
              <p>
                To exercise your rights, email{" "}
                <a 
                  href="mailto:privacy@neilaiglobalsolutionsllc.net" 
                  className="text-primary hover:underline font-semibold"
                >
                  privacy@neilaiglobalsolutionsllc.net
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Third-Party Links */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">8.</span> Third-Party Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                Our website may link to external partners, affiliate programs, or demo videos. 
                We are not responsible for the privacy practices of those sites. 
                We encourage you to review their privacy policies before engaging.
              </p>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">9.</span> Children's Privacy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                Neil AI Global Solutions LLC does not knowingly collect information from children under 13. 
                If you believe we've inadvertently collected such data, please contact us immediately.
              </p>
            </CardContent>
          </Card>

          {/* Changes to This Policy */}
          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl flex items-center gap-2">
                <span className="text-primary">10.</span> Changes to This Policy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                We may update this Privacy Policy periodically. 
                Any changes will be posted on this page with an updated "Effective Date." 
                Continued use of our site means you accept those updates.
              </p>
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <h3 className="font-orbitron text-xl font-bold text-foreground">
                  Questions About This Privacy Policy?
                </h3>
                <p className="text-muted-foreground">
                  We're here to help. Contact our privacy team for any concerns or questions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild variant="default" size="lg">
                    <a href="mailto:privacy@neilaiglobalsolutionsllc.net">
                      Email Privacy Team
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <a href="tel:+13369624539">
                      Call Us: (336) 962-4539
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;
